"use client";

import { useState } from "react";
import { Download, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ebooks = [{
  id: 1,
  title: "The Systematic Studio",
  description: "Eliminate administrative chaos and reclaim your creative time with AI-driven automation systems built specifically for dance studios.",
  cover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1496898/img/systematic_studio_ebook_cover.png",
  downloadUrl: "#"
}, {
  id: 2,
  title: "The NPO Survival Guide",
  description: "Transform nonprofit operations with cutting-edge automation that frees your team to focus on mission-critical work instead of paperwork.",
  cover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1496898/img/npo_survival_guide_ebook_cover.png",
  downloadUrl: "#"
}];
interface Ebook {
  id: number;
  title: string;
  description: string;
  cover: string;
  downloadUrl: string;
}
interface EmailCaptureModalProps {
  ebook: Ebook | null;
  onClose: () => void;
}
function EmailForm({
  ebook,
  onClose
}: EmailCaptureModalProps) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  const onSubmit = async () => {
    await handleSubmit();
    if (!submitError) {
      const link = document.createElement("a");
      link.href = ebook?.downloadUrl || "#";
      link.download = ebook?.title || "guide";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4">
          <Download className="size-8 text-primary" />
        </div>
        <h3 className="text-xl font-display font-bold text-white mb-2">Download Starting...</h3>
        <p className="text-muted-foreground/80">Your guide will download shortly.</p>
      </div>;
  }
  return <Form onSubmit={onSubmit}>
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-display font-bold text-white mb-2">
            Download {ebook?.title}
          </h3>
          <p className="text-white text-sm">
            Enter your email to receive your free guide
          </p>
        </div>

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" className="bg-dark-background/50 border-primary/30 text-white placeholder:text-white/80 focus:border-primary focus:ring-primary/50 transition-all duration-[80ms]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{submitError}</p>
          </div>}

        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1 bg-transparent border-primary/30 text-white hover:bg-primary/10 hover:border-primary transition-all duration-[80ms]" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_rgba(59,130,246,0.7)] hover:scale-[1.04] active:scale-100 transition-all duration-[80ms]">
            {isSubmitting ? <>Submitting...</> : <>
                <Download className="size-4" />
                Download Guide
              </>}
          </Button>
        </div>
      </div>
    </Form>;
}
function EmailCaptureModal({
  ebook,
  onClose
}: EmailCaptureModalProps) {
  if (!ebook) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-dark-background/90 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-background border border-primary/30 rounded-xl shadow-[0_0_40px_rgba(59,130,246,0.3)] max-w-md w-full p-8">
        <Button variant="ghost" size="icon" className="absolute top-4 right-4 text-muted-foreground hover:text-white" onClick={onClose}>
          <X className="size-5" />
        </Button>

        <FormProvider formId="ebook-download-form" sectionName="ebook-collection" defaultValues={{
        email: ""
      }} validationRules={{
        email: {
          required: true,
          pattern: "email"
        }
      }} successMessage="Thank you! Your download will begin shortly.">
          <EmailForm ebook={ebook} onClose={onClose} />
        </FormProvider>
      </div>
    </div>;
}
export default function EBookCollection() {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEbook, setSelectedEbook] = useState<Ebook | null>(null);
  const {
    ref: ref1,
    isVisible: isVisible1
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const {
    ref: ref2,
    isVisible: isVisible2
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const handleDownloadClick = (ebook: Ebook) => {
    setSelectedEbook(ebook);
    setShowEmailModal(true);
  };
  const handleCloseModal = () => {
    setShowEmailModal(false);
    setSelectedEbook(null);
  };
  return <>
      <section className="bg-dark-background py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-12 text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              E‑book Collection
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Download our exclusive guides and start remixing your workflow today
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div ref={ref1} data-index={0} className="transition-all duration-[200ms] ease-[cubic-bezier(0.4,0,0.2,1)]" style={{
            opacity: isVisible1 ? 1 : 0,
            transform: isVisible1 ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "0ms"
          }}>
              <Card className="bg-light-background overflow-hidden group cursor-pointer">
                <div className="relative aspect-[3/4] overflow-hidden border-2 border-secondary rounded-t-xl">
                  <img src={ebooks[0].cover} alt={ebooks[0].title} className="w-full h-full object-cover transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-[1.02]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-background/90 via-dark-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-end p-6">
                    <div className="text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
                      <h3 className="font-display text-xl font-bold mb-2">{ebooks[0].title}</h3>
                      <p className="text-sm text-white/90">{ebooks[0].description}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 shadow-[0_0_20px_rgba(167,139,250,0)] group-hover:shadow-[0_0_20px_rgba(167,139,250,0.4)] transition-all duration-[250ms]" />
                </div>
                <CardContent className="pt-6">
                  <Button className="w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-foreground-primary font-semibold shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] hover:scale-[1.04] active:scale-100 transition-all duration-[80ms]" onClick={() => handleDownloadClick(ebooks[0])}>
                    <Download className="size-4" />
                    Download Guide
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div ref={ref2} data-index={1} className="transition-all duration-[200ms] ease-[cubic-bezier(0.4,0,0.2,1)]" style={{
            opacity: isVisible2 ? 1 : 0,
            transform: isVisible2 ? "translateY(0)" : "translateY(20px)",
            transitionDelay: "100ms"
          }}>
              <Card className="bg-light-background overflow-hidden group cursor-pointer">
                <div className="relative aspect-[3/4] overflow-hidden border-2 border-secondary rounded-t-xl">
                  <img src={ebooks[1].cover} alt={ebooks[1].title} className="w-full h-full object-cover transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-[1.02]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-background/90 via-dark-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-end p-6">
                    <div className="text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
                      <h3 className="font-display text-xl font-bold mb-2">{ebooks[1].title}</h3>
                      <p className="text-sm text-white/90">{ebooks[1].description}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 shadow-[0_0_20px_rgba(167,139,250,0)] group-hover:shadow-[0_0_20px_rgba(167,139,250,0.4)] transition-all duration-[250ms]" />
                </div>
                <CardContent className="pt-6">
                  <Button className="w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-foreground-primary font-semibold shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] hover:scale-[1.04] active:scale-100 transition-all duration-[80ms]" onClick={() => handleDownloadClick(ebooks[1])}>
                    <Download className="size-4" />
                    Download Guide
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {showEmailModal && <EmailCaptureModal ebook={selectedEbook} onClose={handleCloseModal} />}
    </>;
}